import router from 'next/router';
import { Space } from '@goatapp/web-content-components';
import { useEffect, useState } from 'react';

import { bugsnag } from 'shared/utils/services/bugsnag';
import { DEFAULT_ASSET_WIDTH_BEFORE_UI_RENDERS } from 'homepage/enums/contentConstants';
import { getBrandFeedTrackingInfo } from 'homepage/getBrandFeedSpaceTrackingInfo';
import { sendTrackingEvent } from 'tracking/utils';
import { useWindowSize } from 'shared/hooks/useWindowSize';

import { ISpaceArticleSection } from 'shared/types/SpaceArticleSection';
import { IModuleTrackingData } from 'homepage/types/ModuleTrackingData';

interface IBrandedSpaceContent {
  drawerName: string;
  draftId?: number;
  isSingleSpaceBrandPage?: boolean;
  maxHeight?: number;
  maxWidth?: number;
  moduleTrackingData: IModuleTrackingData;
  section: ISpaceArticleSection;
  sectionIndex?: number;
  virtualMaxHeight?: number;
  virtualMaxWidth?: number;
}

export const BrandSpaceContent = ({
  drawerName,
  isSingleSpaceBrandPage = false,
  maxHeight = null,
  maxWidth = null,
  moduleTrackingData,
  section = null,
  draftId,
  virtualMaxHeight = null,
  virtualMaxWidth = null,
}: IBrandedSpaceContent) => {
  const { articles = [] } = section || {};

  const { isMobile, windowSize } = useWindowSize();

  const [canvasWidth, setCanvasWidth] = useState(DEFAULT_ASSET_WIDTH_BEFORE_UI_RENDERS);

  const getCurrentSpace = () => {
    if (articles?.length === 1) {
      return articles?.[0]?.sections?.[0];
    }
    return isMobile ? articles?.[1]?.sections?.[0] : articles?.[0]?.sections?.[0];
  };

  const trackContentClick = (url) => {
    sendTrackingEvent('HOMEPAGE_LANDING_TAP', {
      drawer_name: drawerName,
      target_destination: url,
    });

    try {
      if (draftId) {
        const data = getBrandFeedTrackingInfo(moduleTrackingData, url);

        sendTrackingEvent('CMS_MODULE_ITEM_TAP', { ...data });
      }
    } catch (error) {
      bugsnag(error, { context: 'Error tracking space click' });
    }
  };

  const redirectToArticleAssetUrl = (url: string) => {
    if (!url) {
      return;
    }
    trackContentClick(url);
    void router.push(url);
  };

  useEffect(() => {
    setCanvasWidth(windowSize?.width);
  }, [isMobile, windowSize]);

  if (articles?.length < 1) {
    return null;
  }

  const currentSpace = getCurrentSpace();

  if (isSingleSpaceBrandPage) {
    return (
      <Space
        space={currentSpace}
        maxWidth={canvasWidth}
        onItemLinkClick={redirectToArticleAssetUrl}
      />
    );
  }

  return !isMobile ? (
    <Space
      space={currentSpace}
      maxWidth={maxWidth}
      onItemLinkClick={redirectToArticleAssetUrl}
      virtualMaxWidth={virtualMaxWidth}
    />
  ) : (
    <Space
      space={currentSpace}
      maxHeight={maxHeight}
      maxWidth={maxWidth}
      virtualMaxHeight={virtualMaxHeight}
      virtualMaxWidth={virtualMaxWidth}
      onItemLinkClick={redirectToArticleAssetUrl}
    />
  );
};
